import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  FormControl,
  Grid,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import Header from "../../components/Header";
import { useGetCustomersQuery } from "../../state/api";
import { addEventAction } from "../../redux/actions/eventAction";
import Autocomplete from "@mui/material/Autocomplete";
import ListItemText from "@mui/material/ListItemText";
import { toast } from "react-toastify";
import LoadingIndicator from "../../components/loadingIndicator";
import * as yup from "yup";

const validationSchema = yup.object().shape({
  eventName: yup.string().required("Event name is required"),
  date: yup
    .date()
    .nullable()
    .typeError("Date is required")
    .required("Date is required"),
  startTime: yup
    .string()
    .required("Starting time is required")
    .matches(
      /^(0?[1-9]|1[0-2]):[0-5][0-9] [APap][mM]$/,
      "Invalid time format, it should be HH:MM AM or PM"
    ),
  endTime: yup
    .string()
    .required("Ending time is required")
    .matches(
      /^(0?[1-9]|1[0-2]):[0-5][0-9] [APap][mM]$/,
      "Invalid time format, it should be HH:MM AM or PM"
    )
    .test(
      "is-end-time-greater",
      "End time must be greater than start time",
      function (value) {
        const { startTime } = this.parent;
        if (!startTime || !value) {
          return true; // Let other validations handle empty fields
        }

        // Convert both start and end times to 24-hour format for comparison
        const parseTime = (time) => {
          const [hour, minute, ampm] = time.split(/[:\s]/);
          let parsedHour = parseInt(hour, 10);
          if (ampm.toUpperCase() === "PM" && parsedHour !== 12) {
            parsedHour += 12;
          } else if (ampm.toUpperCase() === "AM" && parsedHour === 12) {
            parsedHour = 0;
          }
          return `${parsedHour.toString().padStart(2, "0")}:${minute}`;
        };

        const parsedStartTime = parseTime(startTime);
        const parsedEndTime = parseTime(value);

        return parsedStartTime < parsedEndTime;
      }
    ),
  location: yup.string().required("Location is required"),
  // users: yup.array().test({
  //   name: "at-least-one-celebrity",
  //   test: (value) => {
  //     return value && value.length > 0;
  //   },
  //   message: "At least one Celebrity is required",
  // }),
});

const CreateEvent = () => {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState({});

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    eventName: "",
    date: "",
    startTime: "",
    endTime: "",
    location: "",
    users: [],
    description: "",
  });

  const { data: usersList = [], isLoading, isError } = useGetCustomersQuery();
  const [selectedUserIds, setSelectedUserIds] = useState([]);

  const handleSelectedUser = (newValue) => {
    setSelectedUserIds(newValue.map((user) => user._id));
    setFormData({
      ...formData,
      users: newValue.map((user) => user.fullname),
    });
  };

  const handleBlur = async (e) => {
    const fieldName = e.target.name;
    try {
      await validationSchema.validateAt(fieldName, formData);
      setErr({ ...err, [fieldName]: "" });
    } catch (error) {
      setErr({ ...err, [fieldName]: error.message });
    }
  };

  const handleChange = (e) => {
    setFormData((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
  };

  const [banner, setBanner] = useState("");
  const [bannerPreview, setbannerPreview] = useState(null);
  const [buttonWidth, setButtonWidth] = useState("100%");

  const handleBannerChnage = (e) => {
    const selectedBanner = e.target.files[0];
    setBanner(selectedBanner);

    if (selectedBanner) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setbannerPreview(event.target.result);
        setButtonWidth("70%");
      };
      reader.readAsDataURL(selectedBanner);
    } else {
      setbannerPreview(null);
      setButtonWidth("100%");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true); // Start loading
      await validationSchema.validate(formData, { abortEarly: false });

      const formDataToSend = new FormData();

      Object.keys(formData).forEach((key) => {
        formDataToSend.append(key, formData[key]);
      });
      formDataToSend.append("banner", banner);

      // Display the key/value pairs
      for (var pair of formDataToSend.entries()) {
        console.log("formAppend", pair[0] + ":" + pair[1]);
      }

      if (banner && banner.size > 2 * 1024 * 1024) {
        setErr({
          ...err,
          banner: "File size is too large (max 2MB)",
        });
        return;
      } else {
        setErr({ ...err, banner: "" });
      }
      const isSuccess = await dispatch(addEventAction(formDataToSend));
      if (isSuccess) {
        toast.success("Event created successfully!");
        setTimeout(() => {
          navigate("/event");
          window.location.reload();
        }, 1000);
      }
    } catch (validationErrors) {
      // Handle validation errors
      const errors = {};
      validationErrors.inner.forEach((validationError) => {
        errors[validationError.path] = validationError.message;
      });
      setErr(errors);
    } finally {
      setLoading(false); // Stop loading
    }
  };

  return (
    <Box mt="1.5rem" mb="2.5rem" ml="2.5rem" mr="2.5rem">
      <Header title="CREATE EVENT" />
      <form onSubmit={handleSubmit}>
        <FormControl fullWidth variant="outlined" margin="normal">
          <Grid container spacing={2}>
            {/* Event Name */}
            <Grid item xs={12}>
              <label htmlFor="eventName">Event Name</label>
              <TextField
                id="eventName"
                name="eventName"
                value={formData.eventName}
                fullWidth
                onBlur={handleBlur}
                margin="normal"
                onChange={handleChange}
              />
              {err.eventName && (
                <Grid item>
                  <Typography color="error">{err.eventName}</Typography>
                </Grid>
              )}{" "}
            </Grid>

            {/* Start Time */}
            <Grid item xs={4}>
              <label htmlFor="startTime">Start Time</label>
              <TextField
                id="startTime"
                name="startTime"
                value={formData.startTime}
                placeholder="HH:MM AM/PM"
                fullWidth
                margin="normal"
                // type="time"
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {err.startTime && (
                <Grid item>
                  <Typography color="error">{err.startTime}</Typography>
                </Grid>
              )}{" "}
            </Grid>

            {/* End Time */}
            <Grid item xs={4}>
              <label htmlFor="endTime">End Time</label>
              <TextField
                id="endTime"
                name="endTime"
                value={formData.endTime}
                placeholder="HH:MM AM/PM"
                onBlur={handleBlur}
                fullWidth
                margin="normal"
                // type="time"
                onChange={handleChange}
              />
              {err.endTime && (
                <Grid item>
                  <Typography color="error">{err.endTime}</Typography>
                </Grid>
              )}{" "}
            </Grid>

            {/* Date */}
            <Grid item xs={4}>
              <label htmlFor="date">Date</label>
              <TextField
                id="date"
                name="date"
                value={formData.date}
                onBlur={handleBlur}
                fullWidth
                margin="normal"
                type="date"
                onChange={handleChange}
              />
              {err.date && (
                <Grid item>
                  <Typography color="error">{err.date}</Typography>
                </Grid>
              )}{" "}
            </Grid>

            {/* Location */}
            <Grid item xs={12}>
              <label htmlFor="location">Location</label>
              <TextField
                id="location"
                name="location"
                value={formData.location}
                onBlur={handleBlur}
                fullWidth
                margin="normal"
                onChange={handleChange}
              />
              {err.location && (
                <Grid item>
                  <Typography color="error">{err.location}</Typography>
                </Grid>
              )}{" "}
            </Grid>

            {/* Celebrity */}
            <Grid item xs={12}>
              <label htmlFor="users">Select the Celebrity</label>
              <Autocomplete
                multiple
                id="users"
                options={usersList}
                // onBlur={handleBlur}
                // disableCloseOnSelect
                getOptionLabel={(option) => option.fullname}
                value={usersList.filter((user) =>
                  selectedUserIds.includes(user._id)
                )}
                onChange={(event, newValue) => {
                  handleSelectedUser(newValue);
                }}
                renderOption={(props, option) => (
                  <li {...props}>
                    <ListItemText primary={option.fullname} />
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    // label="users"
                    placeholder="Celebrity"
                    name="users"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    autoWidth
                  />
                )}
              />
              {/* {err.users && (
                <Grid item>
                  <Typography color="error">{err.users}</Typography>
                </Grid>
              )}{" "} */}
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <label htmlFor="description">Description</label>
              <TextField
                id="description"
                name="description"
                value={formData.description}
                fullWidth
                margin="normal"
                onChange={handleChange}
              />
            </Grid>

            {/* UPLOAD BANNER */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <label
                  htmlFor="banner"
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <Button
                    variant="outlined"
                    color="info"
                    component="span"
                    size="large"
                    style={{
                      width: buttonWidth,
                      fontSize: "1rem",
                      textTransform: "none",
                    }}
                  >
                    Upload a Banner
                  </Button>
                  <input
                    id="banner"
                    name="banner"
                    type="file"
                    onChange={handleBannerChnage}
                    // onBlur={handleBlur}
                    accept="image/*"
                    style={{ display: "none" }}
                  />
                  {/* {err.banner && (
                    <Grid item>
                      <Typography color="error">
                        {err.banner}
                      </Typography>
                    </Grid>
                  )}{" "} */}
                  {bannerPreview && (
                    <img
                      src={bannerPreview}
                      alt="banner"
                      style={{
                        maxWidth: "150px",
                        maxHeight: "70px",
                        marginLeft: "10px",
                      }}
                    />
                  )}
                </label>
                {err.banner && (
                  <Grid item>
                    <Typography color="error">{err.banner}</Typography>
                  </Grid>
                )}{" "}
              </FormControl>
            </Grid>
          </Grid>
        </FormControl>

        {/* SUBMIT BUTTON */}
        <Grid container justifyContent="center" alignItems="center">
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<SendIcon />}
            disabled={loading}
          >
            Submit
          </Button>
        </Grid>
      </form>
      {loading && <LoadingIndicator />}
    </Box>
  );
};

export default CreateEvent;
